import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Text, ActionButton, Loading, Badge,
  IconButton, IconSynchronize, IconArrowLeft, IconDownload,
  IconAdd, IconEdit,
  TextInput, Modal, Checkbox, SelectInput,
} from '@carla/flora'
import { getApiBaseUrl } from '@/app/config/settings'
import { useAuth } from '@/app/hooks/useAuth'
import { useTemplates } from '@/app/hooks/useTemplates'
import { useTemplateMappings, type MappingEntry } from '@/app/hooks/useTemplateMappings'
import { useCarConditionCatalog, catalogEntryLabel, type CarConditionCatalogEntry } from '@/app/hooks/useCarConditionCatalog'
import InspectionTypeBadge from '@/app/components/InspectionTypeBadge'

// ─── Priority column ──────────────────────────────────────────────────────────

const PRIORITY_BADGE: Record<string, { type: 'splash' | 'success' | 'info' | 'warning' | 'destructive'; label: string }> = {
  FLAGGED:      { type: 'destructive', label: 'REPAIR' },
  IMPERFECTION: { type: 'warning',     label: 'NO REPAIR' },
  MINOR:        { type: 'success',     label: 'OTHER' },
  NONE:         { type: 'splash',      label: 'NONE' },
}

function PriorityCell({ entry }: { entry: MappingEntry }) {
  if (entry.mapped === false || !entry.priority) {
    return <Text extend={{ color: '#aaa' }}>—</Text>
  }
  const cfg = PRIORITY_BADGE[entry.priority]
  if (!cfg) return <Text>{entry.priority}</Text>
  return <Badge type={cfg.type}>{cfg.label}</Badge>
}

// ─── Row state logic ──────────────────────────────────────────────────────────

type RowState = 'error_missing' | 'error_multiple' | 'unmapped' | 'normal'

function getRowState(entry: MappingEntry): RowState {
  if (entry.error_multiple) return 'error_multiple'
  if (entry.error_missing || entry.catalog_condition_type === 'Unmapped Value') return 'error_missing'
  if (entry.mapped === false) return 'unmapped'
  return 'normal'
}

const ROW_BG: Record<RowState, string | undefined> = {
  error_missing:  '#fff0f0',
  error_multiple: '#fff7ed',
  unmapped:       '#f7f7f7',
  normal:         undefined,
}

// ─── Status filter ────────────────────────────────────────────────────────────

type StatusFilter = 'mapped' | 'missing_mapping' | 'multiple_matches' | 'unmapped'

const STATUS_FILTER_OPTIONS: { id: StatusFilter; label: string }[] = [
  { id: 'mapped',           label: 'Mapped' },
  { id: 'missing_mapping',  label: 'Missing' },
  { id: 'multiple_matches', label: 'Multiple' },
  { id: 'unmapped',         label: 'Unmapped' },
]

function rowMatchesFilters(state: RowState, active: Set<StatusFilter>): boolean {
  if (active.size === 0) return true
  if (active.has('mapped')           && state === 'normal')          return true
  if (active.has('missing_mapping')  && state === 'error_missing')   return true
  if (active.has('multiple_matches') && state === 'error_multiple')  return true
  if (active.has('unmapped')         && state === 'unmapped')        return true
  return false
}

interface StatusFilterPillsProps {
  selected: Set<StatusFilter>
  onChange: (next: Set<StatusFilter>) => void
}

function StatusFilterPills({ selected, onChange }: StatusFilterPillsProps) {
  const toggle = (id: StatusFilter) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    onChange(next)
  }

  return (
    <Box direction="row" space={1} alignItems="center">
      {STATUS_FILTER_OPTIONS.map((opt) => (
        <ActionButton
          key={opt.id}
          mode="compact"
          intent={selected.has(opt.id) ? 'primary' : 'control'}
          onClick={() => toggle(opt.id)}
        >
          {opt.label}
        </ActionButton>
      ))}
    </Box>
  )
}

// ─── Catalog combobox ─────────────────────────────────────────────────────────

const COMBOBOX_MAX_RESULTS = 50

interface CatalogComboboxProps {
  entries: CarConditionCatalogEntry[]
  value: string
  onChange: (id: string) => void
  loading?: boolean
}

type DropdownPos = { top?: number; bottom?: number; left: number; width: number }

const DROPDOWN_MAX_HEIGHT = 280

function CatalogCombobox({ entries, value, onChange, loading }: CatalogComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [dropdownPos, setDropdownPos] = useState<DropdownPos>({ top: 0, left: 0, width: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedEntry = entries.find((e) => e.id === value)

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return entries.slice(0, COMBOBOX_MAX_RESULTS)
    return entries
      .filter((e) => catalogEntryLabel(e).toLowerCase().includes(needle))
      .slice(0, COMBOBOX_MAX_RESULTS)
  }, [entries, query])

  const updatePos = () => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    if (spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow) {
      setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width })
    } else {
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width })
    }
  }

  // Close on outside click; re-position on scroll/resize while open
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
    if (!open) { updatePos(); setOpen(true) }
  }

  const handleFocus = () => {
    updatePos()
    setOpen(true)
    setQuery('')
  }

  const handleSelect = (id: string) => {
    onChange(id)
    setOpen(false)
    setQuery('')
  }

  const displayValue = open ? query : (selectedEntry ? catalogEntryLabel(selectedEntry) : '')

  const dropdown = open && !loading && createPortal(
    <Box
      bg="background.primary"
      extend={{
        position: 'fixed',
        ...(dropdownPos.bottom !== undefined
          ? { bottom: dropdownPos.bottom }
          : { top: dropdownPos.top }),
        left: dropdownPos.left,
        width: dropdownPos.width,
        border: '1px solid #e5e5e5',
        borderRadius: 6,
        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        zIndex: 100000,
        maxHeight: DROPDOWN_MAX_HEIGHT,
        overflowY: 'auto',
      }}
    >
      {filtered.length === 0 ? (
        <Box padding={3}>
          <Text color="foreground.secondary">No matches</Text>
        </Box>
      ) : (
        filtered.map((entry) => {
          const isSelected = entry.id === value
          return (
            <Box
              key={entry.id}
              padding={3}
              onMouseDown={() => handleSelect(entry.id)}
              bg={isSelected ? 'background.info' : 'background.primary'}
              extend={{
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                ':hover': { background: '#f5f5f5' },
              }}
            >
              <Text subStyle={isSelected ? 'emphasis' : undefined}>
                {catalogEntryLabel(entry)}
              </Text>
            </Box>
          )
        })
      )}
      {filtered.length === COMBOBOX_MAX_RESULTS && (
        <Box padding={3} extend={{ borderTop: '1px solid #eee' }}>
          <Text variant="birch" color="foreground.secondary">
            Showing first {COMBOBOX_MAX_RESULTS} — type to narrow down
          </Text>
        </Box>
      )}
    </Box>,
    // Portal into the <dialog> element when one is open (Flora Modal uses showModal() which
    // places it in the browser top layer — any portal to document.body renders behind it
    // regardless of z-index). Falling back to document.body for non-modal usage.
    containerRef.current?.closest('dialog') ?? document.body
  )

  return (
    <div ref={containerRef} style={{ flex: 1 }}>
      <TextInput
        name="catalog-search"
        label="Catalog item"
        placeholder={loading ? 'Loading…' : 'Search catalog…'}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        isEmpty={(v) => !v}
        disabled={loading}
      />
      {dropdown}
    </div>
  )
}

// ─── Mappings table ───────────────────────────────────────────────────────────

const PAGE_SIZE = 20

const TH: React.CSSProperties = {
  padding: '10px 14px',
  textAlign: 'left',
  borderBottom: '2px solid #e5e5e5',
  whiteSpace: 'nowrap',
  background: '#f9f9f9',
}

interface MappingsTableProps {
  entries: MappingEntry[]
  page: number
  onEdit: (entry: MappingEntry) => void
}

function MappingsTable({ entries, page, onEdit }: MappingsTableProps) {
  const pageEntries = entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  if (pageEntries.length === 0) {
    return (
      <Box padding={4} alignItems="center">
        <Text color="foreground.secondary">No entries match the current filters.</Text>
      </Box>
    )
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <colgroup>
        <col style={{ width: '30%' }} />
        <col style={{ width: '13%' }} />
        <col style={{ width: '18%' }} />
        <col style={{ width: '20%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '7%' }} />
      </colgroup>
      <thead>
        <tr>
          <th style={TH}><Text subStyle="emphasis">Label</Text></th>
          <th style={TH}><Text subStyle="emphasis">Priority</Text></th>
          <th style={TH}><Text subStyle="emphasis">Area</Text></th>
          <th style={TH}><Text subStyle="emphasis">Condition type</Text></th>
          <th style={TH}><Text subStyle="emphasis">Extent</Text></th>
          <th style={TH} />
        </tr>
      </thead>
      <tbody>
        {pageEntries.map((entry, i) => {
          const state = getRowState(entry)
          const bg = ROW_BG[state]
          const cell: React.CSSProperties = {
            padding: '10px 14px',
            borderBottom: '1px solid #eee',
            verticalAlign: 'middle',
            background: bg,
          }
          const isAdd = !entry.catalog_id
          return (
            <tr key={i}>
              <td style={cell}><Text>{entry.safety_culture_label}</Text></td>
              <td style={cell}><PriorityCell entry={entry} /></td>
              <td style={cell}><Text>{entry.catalog_area ?? '—'}</Text></td>
              <td style={cell}><Text>{entry.catalog_condition_type ?? '—'}</Text></td>
              <td style={cell}><Text>{entry.catalog_extent ?? '—'}</Text></td>
              <td style={{ ...cell, textAlign: 'right' }}>
                <IconButton
                  icon={isAdd ? IconAdd : IconEdit}
                  label={isAdd ? 'Add mapping' : 'Edit mapping'}
                  onClick={() => onEdit(entry)}
                  size={32}
                  iconSize={16}
                  color="foreground.secondary"
                />
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TemplateDetailPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()
  const { getFirebaseToken } = useAuth()

  const { data: templatesData, isLoading: templatesLoading } = useTemplates(getFirebaseToken)
  const { data: mappingsData, isLoading: mappingsLoading, error: mappingsError, loadMappings, patchEntry } =
    useTemplateMappings(getFirebaseToken)
  const { data: catalogData, isLoading: catalogLoading, refresh: refreshCatalog } =
    useCarConditionCatalog(getFirebaseToken)

  const [search, setSearch] = useState('')
  const [statusFilters, setStatusFilters] = useState<Set<StatusFilter>>(
    new Set<StatusFilter>(['missing_mapping', 'multiple_matches'])
  )
  const [page, setPage] = useState(1)
  const [editEntry, setEditEntry] = useState<MappingEntry | null>(null)
  const [modalValue, setModalValue] = useState('')
  const [modalMapped, setModalMapped] = useState(false)
  const [modalPriority, setModalPriority] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const template = templatesData?.templates.find((t) => t.template_id === templateId)

  const filteredEntries = useMemo(() => {
    if (!mappingsData) return []
    const needle = search.toLowerCase()
    return mappingsData.entries
      .filter((e) => {
        const state = getRowState(e)
        if (!rowMatchesFilters(state, statusFilters)) return false
        if (needle && !e.safety_culture_label.toLowerCase().includes(needle)) return false
        return true
      })
      .sort((a, b) => a.safety_culture_label.localeCompare(b.safety_culture_label))
  }, [mappingsData, search, statusFilters])

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE))

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handleFiltersChange = (next: Set<StatusFilter>) => {
    setStatusFilters(next)
    setPage(1)
  }

  const handleLoadMappings = () => {
    if (templateId) loadMappings(templateId)
  }

  const handleOpenEdit = (entry: MappingEntry) => {
    setEditEntry(entry)
    setModalValue(entry.catalog_id ?? '')
    setModalMapped(entry.mapped ?? false)
    setModalPriority(entry.priority ?? '')
  }

  const handleCloseModal = () => {
    setEditEntry(null)
    setModalValue('')
    setModalMapped(false)
    setModalPriority('')
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!editEntry) return
    setIsSaving(true)
    setSaveError(null)
    try {
      const token = await getFirebaseToken()
      const base = getApiBaseUrl()
      if (!base) throw new Error('API base URL not configured')
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      }
      const isUpdate = !!editEntry.id
      let newId: string | undefined
      if (isUpdate) {
        const body: Record<string, unknown> = { id: editEntry.id, mapped: modalMapped }
        if (modalValue)    body.catalog_id = modalValue
        if (modalPriority) body.priority   = modalPriority
        const response = await fetch(`${base}/safetyculture/mappings/update`, {
          method: 'POST', mode: 'cors', credentials: 'include', headers,
          body: JSON.stringify(body),
        })
        if (!response.ok) throw new Error(`Failed to update mapping: ${response.status}`)
      } else {
        const body: Record<string, unknown> = {
          safety_culture_label: editEntry.safety_culture_label,
          mapped: modalMapped,
        }
        if (modalValue)          body.catalog_id  = modalValue
        if (modalPriority)       body.priority    = modalPriority
        if (template?.flow_code) body.target_flow = template.flow_code.toUpperCase()
        const response = await fetch(`${base}/safetyculture/mappings`, {
          method: 'POST', mode: 'cors', credentials: 'include', headers,
          body: JSON.stringify(body),
        })
        if (!response.ok) throw new Error(`Failed to add mapping: ${response.status}`)
        const json = await response.json()
        newId = json?.id
      }
      // Local update — no full reload
      patchEntry(editEntry.safety_culture_label, {
        ...(newId ? { id: newId } : {}),
        mapped: modalMapped,
        catalog_id: modalValue || null,
        priority: modalPriority || null,
        error_missing: !modalValue,
        error_multiple: !modalValue,
      })
      handleCloseModal()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save mapping')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box padding={5} space={5}>
      {/* Back */}
      <Box>
        <IconButton
          icon={IconArrowLeft}
          label="Back to templates"
          onClick={() => navigate('/')}
          size={32}
          iconSize={18}
          color="foreground.secondary"
        />
      </Box>

      {/* Template details card */}
      {templatesLoading && (
        <Box alignItems="center" padding={4}><Loading /></Box>
      )}
      {template && (
        <Box
          extend={{
            background: '#f9f9f9',
            borderRadius: '8px',
            padding: '20px 24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, auto)',
            gap: '24px',
            alignItems: 'center',
          }}
        >
          <Box space={1}>
            <Text variant="birch" color="foreground.secondary">Flow name</Text>
            <Text subStyle="emphasis">{template.flow_name ?? '—'}</Text>
          </Box>
          <Box space={1}>
            <Text variant="birch" color="foreground.secondary">Flow code</Text>
            <Text subStyle="emphasis">{template.flow_code.toUpperCase()}</Text>
          </Box>
          <Box space={1}>
            <Text variant="birch" color="foreground.secondary">Inspection type</Text>
            <InspectionTypeBadge value={template.inspection_type} />
          </Box>
          <Box space={1}>
            <Text variant="birch" color="foreground.secondary">Template ID</Text>
            <Text subStyle="emphasis" extend={{ wordBreak: 'break-all' }}>{template.template_id}</Text>
          </Box>
        </Box>
      )}

      {/* Mappings section */}
      <Box space={4}>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Text variant="ash">Mappings</Text>
          <Box direction="row" space={2} alignItems="center">
            {!mappingsData && !mappingsLoading && (
              <IconButton
                icon={IconDownload}
                label="Load mappings"
                onClick={handleLoadMappings}
                disabled={mappingsLoading}
                size={32}
                iconSize={18}
                color="foreground.secondary"
              />
            )}
            {(mappingsData || mappingsError) && (
              <IconButton
                icon={IconSynchronize}
                label="Reload mappings"
                onClick={handleLoadMappings}
                disabled={mappingsLoading}
                size={32}
                iconSize={18}
                color="foreground.secondary"
              />
            )}
          </Box>
        </Box>

        {/* Loading state */}
        {mappingsLoading && (
          <Box alignItems="center" justifyContent="center" padding={5} space={3}>
            <Loading />
            <Text color="foreground.secondary">Loading mappings, this may take up to 30 seconds…</Text>
          </Box>
        )}

        {/* Error state */}
        {mappingsError && !mappingsLoading && (
          <Box space={3}>
            <Text color="background.destructive">Error: {mappingsError.message}</Text>
            <ActionButton onClick={handleLoadMappings} intent="control">Retry</ActionButton>
          </Box>
        )}

        {/* Prompt before first load */}
        {!mappingsData && !mappingsLoading && !mappingsError && (
          <Box padding={4} alignItems="center">
            <Text color="foreground.secondary">
              Click the download icon above to fetch mapping data for this template.
            </Text>
          </Box>
        )}

        {/* Results */}
        {mappingsData && !mappingsLoading && (
          <>
            {/* Filters row */}
            <Box direction="row" space={3} alignItems="flex-end" extend={{ flexWrap: 'wrap' }}>
              <Box extend={{ width: 220, flexShrink: 0 }}>
                <TextInput
                  name="label-search"
                  label="Search label"
                  placeholder="Filter by label…"
                  value={search}
                  onChange={handleSearchChange}
                  isEmpty={(v) => !v}
                  type="search"
                />
              </Box>
              <StatusFilterPills selected={statusFilters} onChange={handleFiltersChange} />
            </Box>

            {/* Legend + count */}
            <Box direction="row" justifyContent="space-between" alignItems="center">
              <Box direction="row" space={3} alignItems="center">
                <Box direction="row" space={1} alignItems="center">
                  <Box extend={{ width: 10, height: 10, borderRadius: 2, background: '#fca5a5' }} />
                  <Text variant="birch" color="foreground.secondary">Missing</Text>
                </Box>
                <Box direction="row" space={1} alignItems="center">
                  <Box extend={{ width: 10, height: 10, borderRadius: 2, background: '#fdba74' }} />
                  <Text variant="birch" color="foreground.secondary">Multiple</Text>
                </Box>
                <Box direction="row" space={1} alignItems="center">
                  <Box extend={{ width: 10, height: 10, borderRadius: 2, background: '#ebebeb' }} />
                  <Text variant="birch" color="foreground.secondary">Unmapped</Text>
                </Box>
              </Box>
              <Text variant="birch" color="foreground.secondary">
                {filteredEntries.length} row{filteredEntries.length !== 1 ? 's' : ''}
              </Text>
            </Box>

            <MappingsTable entries={filteredEntries} page={page} onEdit={handleOpenEdit} />

            {/* Pagination */}
            {totalPages > 1 && (
              <Box direction="row" justifyContent="space-between" alignItems="center" paddingVertical={2}>
                <Text variant="birch" color="foreground.secondary">
                  Page {page} of {totalPages} · showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredEntries.length)}–{Math.min(page * PAGE_SIZE, filteredEntries.length)} of {filteredEntries.length}
                </Text>
                <Box direction="row" space={2}>
                  <ActionButton
                    mode="compact"
                    intent="control"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    ← Prev
                  </ActionButton>
                  <ActionButton
                    mode="compact"
                    intent="control"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next →
                  </ActionButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Edit / Add modal */}
      {editEntry && (
        <Modal
          visible={!!editEntry}
          onClose={handleCloseModal}
          title={editEntry.id ? 'Update mapping' : 'Add mapping'}
          actions={[
            <ActionButton key="cancel" intent="control" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </ActionButton>,
            <ActionButton key="save" intent="primary" onClick={handleSave} loading={isSaving}>
              {editEntry.id ? 'Update' : 'Save'}
            </ActionButton>,
          ]}
        >
          <Box space={4}>
            <Box space={1}>
              <Text variant="birch" color="foreground.secondary">Label</Text>
              <Text subStyle="emphasis">{editEntry.safety_culture_label}</Text>
            </Box>
            <Checkbox
              name="mapped"
              value={modalMapped}
              onChange={(e) => setModalMapped(e.target.checked)}
              label="Mapped"
            />
            <Box direction="row" space={2} alignItems="flex-end">
              <CatalogCombobox
                entries={catalogData}
                value={modalValue}
                onChange={setModalValue}
                loading={catalogLoading}
              />
              <Box extend={{ paddingBottom: 2, flexShrink: 0 }}>
                <IconButton
                  icon={IconSynchronize}
                  label="Refresh catalog"
                  onClick={refreshCatalog}
                  disabled={catalogLoading}
                  size={40}
                  iconSize={18}
                  color="foreground.secondary"
                />
              </Box>
            </Box>
            <SelectInput
              name="priority"
              label="Priority"
              value={modalPriority}
              onChange={(e) => setModalPriority(e.target.value)}
              isEmpty={(v) => !v}
            >
              <option value="" disabled>Select priority…</option>
              <option value="FLAGGED">REPAIR</option>
              <option value="IMPERFECTION">NO REPAIR</option>
              <option value="MINOR">OTHER</option>
              <option value="NONE">NONE</option>
            </SelectInput>
            {saveError && (
              <Text color="background.destructive">{saveError}</Text>
            )}
          </Box>
        </Modal>
      )}
    </Box>
  )
}
